import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Package,
  Edit,
  ArrowUpDown,
  Pill,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const statusColors = {
  in_stock: "bg-green-100 text-green-700 border-green-200",
  active: "bg-green-100 text-green-700 border-green-200",
  low_stock: "bg-orange-100 text-orange-700 border-orange-200",
  out_of_stock: "bg-red-100 text-red-700 border-red-200",
  expired: "bg-red-100 text-red-700 border-red-200",
  expiring_soon: "bg-amber-100 text-amber-700 border-amber-200",
  discontinued: "bg-gray-100 text-gray-700 border-gray-200",
};

// Priority order for displaying single status (highest priority first)
const statusPriority = ['expired', 'out_of_stock', 'expiring_soon', 'low_stock', 'in_stock', 'active'];

const getPrimaryStatus = (statuses) => {
  if (!statuses || statuses.length === 0) return null;
  for (const status of statusPriority) {
    if (statuses.includes(status)) return status;
  }
  return statuses[0]; // Fallback to first status
};

const categoryColors = {
  medication: "bg-blue-100 text-blue-700",
  supplement: "bg-purple-100 text-purple-700",
  hormone: "bg-pink-100 text-pink-700",
  vitamin: "bg-yellow-100 text-yellow-700",
  medical_supply: "bg-indigo-100 text-indigo-700",
  equipment: "bg-teal-100 text-teal-700",
};

export default function StockList({
  items,
  isLoading,
  onEditItem,
  onTransaction,
  onDeleteItem,
}) {
  if (isLoading) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg">
        <CardContent className="p-6">
          <div className="space-y-4">
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <Skeleton className="w-12 h-12 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  </div>
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg">
        <CardContent className="p-12 text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            No Stock Items Found
          </h3>
          <p className="text-slate-600">
            Add your first inventory item to get started
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg">
      <CardContent className="p-0">
        <div className="w-full overflow-auto max-h-[70vh]">
          <table className="w-full border-collapse" style={{ minWidth: "1190px" }}>
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="w-[300px] min-w-[300px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Item Details</th>
                <th className="w-[120px] min-w-[120px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Category</th>
                <th className="w-[120px] min-w-[120px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Stock Level</th>
                <th className="w-[150px] min-w-[150px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Status</th>
                <th className="w-[140px] min-w-[140px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Location</th>
                <th className="w-[130px] min-w-[130px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Expiration</th>
                <th className="w-[130px] min-w-[130px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Date Added</th>
                <th className="w-[100px] min-w-[100px] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase bg-slate-50">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/60 border-b border-slate-100">
                  <td className="w-[300px] min-w-[300px] px-4 py-3">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Pill className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">
                          {item.name}
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <div className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">
                            Pack: {item.packSize || "N/A"}
                          </div>
                          <div className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">
                            Batch: {item.batchNumber}
                          </div>
                          <div className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                            Exp: {item.expiryDate ? formatDate(item.expiryDate, "MMM yyyy") : "N/A"}
                          </div>
                          {item.supplier && (
                            <div className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                              {item.supplier}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="w-[120px] min-w-[120px] px-4 py-3">
                    <Badge
                      className="bg-blue-100 text-blue-700 border-0"
                    >
                      Medicine
                    </Badge>
                  </td>

                  <td className="w-[120px] min-w-[120px] px-4 py-3">
                    <div className="space-y-1">
                      <div className="font-medium">
                        {item.currentStock} packs
                      </div>
                      {item.reorderPoint > 0 && (
                        <div className="text-xs text-slate-500">
                          Reorder at: {item.reorderPoint}
                        </div>
                      )}
                      {item.minimumStock > 0 && (
                        <div className="text-xs text-slate-500">
                          Min: {item.minimumStock}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="w-[150px] min-w-[150px] px-4 py-3">
                    {(() => {
                      const primaryStatus = getPrimaryStatus(item.statuses);
                      return primaryStatus ? (
                        <Badge
                          className={`${statusColors[primaryStatus] || statusColors.in_stock} border font-medium`}
                        >
                          {primaryStatus.replace(/_/g, " ")}
                        </Badge>
                      ) : null;
                    })()}
                  </td>

                  <td className="w-[140px] min-w-[140px] px-4 py-3">
                    <div className="text-sm">
                      {item.storageLocation || "Not specified"}
                    </div>
                    {item.storageRequirements && (
                      <div className="text-xs text-slate-500">
                        {item.storageRequirements}
                      </div>
                    )}
                  </td>

                  <td className="w-[130px] min-w-[130px] px-4 py-3">
                    <div className="text-sm">
                      {item.expiryDate ? formatDate(item.expiryDate, "MMM d, yyyy") : "N/A"}
                    </div>
                  </td>

                  <td className="w-[130px] min-w-[130px] px-4 py-3">
                    <div className="text-sm">
                      {item.createdAt ? formatDate(item.createdAt, "MMM d, yyyy") : "N/A"}
                    </div>
                  </td>

                  <td className="w-[100px] min-w-[100px] px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEditItem(item)}>
                          <Edit className="mr-2 h-4 w-4" />
                          <span>Edit</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onTransaction(item)}>
                          <ArrowUpDown className="mr-2 h-4 w-4" />
                          <span>Transaction</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDeleteItem(item.id)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
